/**
 * Whether a thing somebody asked to look at can be shown, and how.
 *
 * The one decision the viewer makes, and it is made from the content type the
 * row recorded — never from a filename a collector typed. The outcomes are a
 * union so an unavailable slide cannot carry an empty `src` and an inline one
 * cannot exist without a URL; the four ways of being unavailable each get
 * their own sentence from `spotlight.constants.ts`. The opening and closing
 * is `useSpotlight.ts`'s, and decoding the two formats browsers will not
 * draw is `spotlight.decode.ts`'s.
 *
 * A missing URL is checked before the type. A PDF nobody could sign is a
 * document this account was not given, not a frame pointed at nothing.
 */

import {
  FRAMEABLE_TYPES,
  SPOTLIGHT_COPY,
  VIEWABLE_IMAGE_TYPES,
} from "./spotlight.constants.ts";
import { DECODABLE_TYPES } from "./spotlight.decode.ts";

/**
 * A thing somebody has asked to look at, before it is known whether it can be
 * looked at.
 *
 * `contentType` is what the row says the bytes are. Never a filename extension:
 * that is a string a collector typed, and it is wrong often enough that a
 * viewer trusting it would render a PDF into an `<img>`.
 */
export interface SpotlightSource {
  id: string;
  caption: string;
  contentType: string;
  /** Null when nothing signed it — storage refused, or nothing asked. */
  url: string | null;
}

/**
 * How one thing is put on screen.
 *
 * A union rather than a flag on a general slide, so `unavailable` cannot carry
 * an empty `src` and an `inline` cannot exist without a URL to point at.
 */
export type SpotlightItem =
  | { kind: "image"; id: string; caption: string; url: string }
  /** Rendered in a frame — the browser's own PDF viewer, for zero extra bytes. */
  | { kind: "inline"; id: string; caption: string; url: string }
  /**
   * Shown, but only after this app has decoded it: TIFF and HEIC.
   *
   * A kind of its own rather than an `image` with a flag, because the two are
   * not the same act. An image slide has its bytes; this one has a fetch, a
   * decode and two ways to fail, and the slide that renders it has to say so.
   */
  | {
      kind: "decodable";
      id: string;
      caption: string;
      url: string;
      /** Normalised, and the decoder branches on it. */
      contentType: string;
    }
  | {
      kind: "unavailable";
      id: string;
      caption: string;
      /** Why, in words. Four different causes, four different sentences. */
      reason: string;
      /** Present when the bytes exist and only the rendering is impossible. */
      url: string | null;
    };

export function toSpotlightItems(sources: readonly SpotlightSource[]): SpotlightItem[] {
  return sources.map(toSpotlightItem);
}

/**
 * One source, decided.
 *
 * The order matters. A missing URL is checked FIRST, because a PDF nobody could
 * sign is not a frame pointed at an empty string — it is a document this
 * account was not given, and it has its own sentence.
 */
export function toSpotlightItem(source: SpotlightSource): SpotlightItem {
  const { id, caption, url } = source;

  if (url === null) {
    return { kind: "unavailable", id, caption, reason: SPOTLIGHT_COPY.refused, url: null };
  }

  const type = normaliseType(source.contentType);

  if (type === "") {
    return { kind: "unavailable", id, caption, reason: SPOTLIGHT_COPY.untyped, url };
  }
  if (VIEWABLE_IMAGE_TYPES.includes(type)) return { kind: "image", id, caption, url };
  if (FRAMEABLE_TYPES.includes(type)) return { kind: "inline", id, caption, url };
  // Before the "no browser can draw this" fallback, because for these two it is
  // no longer true — this app draws them. See `DECODABLE_TYPES`.
  if (DECODABLE_TYPES.includes(type)) {
    return { kind: "decodable", id, caption, url, contentType: type };
  }

  // An accepted upload the browser cannot decode reads differently from a type
  // that should never have been stored, and the reader can act on the first.
  const reason = isUndisplayableImage(type)
    ? SPOTLIGHT_COPY.undisplayable(type)
    : SPOTLIGHT_COPY.unknown(type);

  return { kind: "unavailable", id, caption, reason, url };
}

/**
 * 'IMAGE/JPEG; charset=binary' → 'image/jpeg'.
 *
 * A content type is a header value: it is case-insensitive and may carry
 * parameters after a semicolon. Matching the raw string against a list would
 * send a perfectly ordinary JPEG down the "cannot display" path.
 */
function normaliseType(raw: string): string {
  const [essence = ""] = raw.split(";");
  return essence.trim().toLowerCase();
}

/** An image format the collector was allowed to upload and no browser will show. */
function isUndisplayableImage(type: string): boolean {
  return type.startsWith("image/");
}
